import { IPlatform } from '@/types';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { Hits, Index, SearchBox, connectHits } from 'react-instantsearch-dom';
import defaultIcon from '../../../public/assets/image-placeholder.png';
import { appSynonymGlobal } from '../../globals/appSynonym.global';
import { SearchStats } from '../searchStats/searchStats.comp';
import { SystemIcon } from '../systemIcon/systemIcon.comp';
import { StyledDiv } from './searchBox.styles';

const CategoriesHitsWrapper = connectHits(({ hits }) =>
	hits.length > 0 ? (
		<>
			<h6>
				Categories{' '}
				<span>
					<SearchStats /> results
				</span>
			</h6>
			<Hits hitComponent={AutoCompleteCategoryHit} />
		</>
	) : (
		<></>
	)
);

const AppsHitsWrapper = connectHits(({ hits }) =>
	hits.length > 0 ? (
		<>
			<h6>
				Apps{' '}
				<span>
					<SearchStats /> results
				</span>
			</h6>
			<Hits hitComponent={AutoCompleteHit} />
		</>
	) : (
		<></>
	)
);

const AutoCompleteHit = ({
	hit,
}: {
	hit: {
		icon: any;
		platform: string;
		slug: string;
		name: string;
		objectID: string;
	};
}) => {
	return (
		<div className="autocomplete-item flex-wrapper" key={`ac_${hit.objectID}`}>
			<Image
				width={30}
				height={30}
				src={hit.icon || defaultIcon}
				alt={hit.name}
			/>
			<div className="meta">
				{(hit?.name || '').replace(/\&#x2011;/g, '- ')}
			</div>
			<Link
				href={`/${hit.platform}/${
					(appSynonymGlobal as any)[hit.platform] || 'app'
				}/${hit.slug}`}
			></Link>
		</div>
	);
};

const AutoCompleteCategoryHit = ({ hit }: { hit: any }) => {
	return (
		<div className="autocomplete-item flex-wrapper" key={`ac_${hit.objectID}`}>
			<div className="meta">
				{hit.displayName} for {hit.platform}
			</div>
			<Link href={`/${hit.platform}/category/${hit.slug}`}></Link>
		</div>
	);
};

export const LocalSearchBox = ({
	platform = null,
	searchAsYouType = false,
	enableAutoComplete = false,
	platforms = null,
	autoFocus = true,
	initialQuery = null,
	placeholder = 'Search...',
	showCategories = true,
}: any) => {
	const { q } = queryString.parse(
		typeof window !== 'undefined' ? window?.location.search : ''
	);
	const [autoCompleteOpened, setAutoCompleteOpened] = useState<boolean>(false);
	const [localQuery, setLocalQuery] = useState<string>((q as string) || '');
	const navigate = useRouter();

	function submit(e: any) {
		e.preventDefault();
		const query = e.target[0].value;
		navigate.push(
			`/${
				(platform as any)?.name ? '/' + (platform as any)?.name : ''
			}/results?q=${query}&orderBy=popular`
		);
	}

	function handleOutsideClick(e: MouseEvent) {
		e.stopPropagation();

		const target = e.target as HTMLElement;
		if (target && !target.closest('.search-box-wrapper')) {
			setAutoCompleteOpened(false);
		}
	}

	useEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}

		if (typeof window !== 'undefined') {
			window.addEventListener('mousedown', handleOutsideClick);

			return () => {
				window.removeEventListener('mousedown', handleOutsideClick);
			};
		}
	}, []);

	return (
		<StyledDiv>
			<div className="search-box">
				<div className="search-box-wrapper">
					<SearchBox
						defaultRefinement={initialQuery || localQuery}
						autoFocus={autoFocus}
						searchAsYouType={searchAsYouType}
						showLoadingIndicator={true}
						submit={<SystemIcon type="search" />}
						reset={<SystemIcon type="close" />}
						loadingIndicator={<></>}
						translations={{
							submitTitle: 'Submit',
							resetTitle: 'Clear',
							placeholder,
						}}
						// focusShortcuts={string[]}
						onSubmit={submit}
						onReset={(e) => {
							if (searchAsYouType && !enableAutoComplete) {
								navigate.push(
									`/${
										(platform as any)?.name ? '/' + (platform as any)?.name : ''
									}/results?q=&orderBy=popular`
								);
							}
						}}
						onChange={(e: any) => {
							if (enableAutoComplete) {
								setLocalQuery(e.target.value);
								setAutoCompleteOpened(!!e.target.value);
							} else {
								if (searchAsYouType) {
									navigate.push(
										`/${
											(platform as any)?.name
												? '/' + (platform as any)?.name
												: ''
										}/results?q=${e.target.value}&orderBy=popular`
									);
								}
							}
						}}
					/>
					{localQuery && autoCompleteOpened && (
						<div className="autocomplete-box">
							{showCategories && (
								<Index
									indexName={
										process.env.NEXT_PUBLIC_ALGOLIA_CATEGORIES_INDEX as string
									}
								>
									<CategoriesHitsWrapper />
								</Index>
							)}
							{!platform && platforms && (
								<>
									<h6>Platforms</h6>
									<div className="ais-Hits-list">
										{(platforms as IPlatform[]).map((p) => (
											<div
												className="autocomplete-item flex-wrapper"
												key={`ac_${p.name}`}
											>
												<div className="meta">
													{localQuery} in {p.displayName}
												</div>
												<Link
													href={`/${p.name}/results?q=${localQuery}&orderBy=popular`}
												></Link>
											</div>
										))}
									</div>
								</>
							)}
							<Index
								indexName={process.env.NEXT_PUBLIC_ALGOLIA_APPS_INDEX as string}
							>
								<AppsHitsWrapper />
							</Index>
						</div>
					)}
				</div>
			</div>
		</StyledDiv>
	);
};
